import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
} from '@/components/ui'
import { fetchCargo, fetchColaborador } from '@/features/rh/api'
import type { CargoDetalhe, ColaboradorDetalhe, DependenteItem } from '@/features/rh/types'
import {
  agendarFerias,
  aprovarFerias,
  fetchFerias,
  fetchFeriasSaldoPainel,
  recusarFerias,
  simularFerias,
  type FeriasCalculo,
} from './api'
import type { FeriasPrioridade, FeriasSaldoItem } from './types'
import { formatDate, formatMoney } from './utils'
import './ferias.css'

function numSalario(v: unknown): number | null {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'))
  return Number.isFinite(n) && n > 0 ? n : null
}

/** Preferência: salário do colaborador → faixa mínima do cargo → média da faixa. */
function salarioBaseDoCargo(
  colab: ColaboradorDetalhe,
  cargo?: CargoDetalhe | null,
): { valor: number | null; origem: string; cargoNome: string } {
  const cargoNome =
    cargo?.nome || colab.cargo_descritivo?.nome || colab.cargo_nome || 'Sem cargo'
  const doColab = numSalario(colab.salario_base)
  if (doColab != null) {
    return {
      valor: doColab,
      origem: `Salário base — ${cargoNome}`,
      cargoNome,
    }
  }
  if (cargo) {
    const min = numSalario(cargo.salario_min ?? cargo.salario_hc_min)
    const max = numSalario(cargo.salario_max ?? cargo.salario_hc_max)
    if (min != null && max != null) {
      const mid = Math.round(((min + max) / 2) * 100) / 100
      return { valor: mid, origem: `Faixa do cargo ${cargoNome} (média)`, cargoNome }
    }
    if (min != null) {
      return { valor: min, origem: `Faixa mínima do cargo ${cargoNome}`, cargoNome }
    }
    if (max != null) {
      return { valor: max, origem: `Faixa máxima do cargo ${cargoNome}`, cargoNome }
    }
  }
  return { valor: null, origem: 'Cargo sem faixa salarial cadastrada', cargoNome }
}

function dependentesIrrf(deps?: DependenteItem[] | null) {
  if (!deps?.length) return 0
  return deps.filter((d) => d.ir).length
}

type Aba = 'painel' | 'planejamento' | 'calculadora' | 'solicitacoes' | 'relatorios' | 'automacoes'

const ABAS: { id: Aba; label: string }[] = [
  { id: 'painel', label: 'Painel geral' },
  { id: 'planejamento', label: 'Planejamento' },
  { id: 'calculadora', label: 'Calculadora' },
  { id: 'solicitacoes', label: 'Solicitações' },
  { id: 'relatorios', label: 'Relatórios' },
  { id: 'automacoes', label: 'Automações' },
]

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

const AUTOS_SEED = [
  {
    id: 'a90',
    titulo: 'Alerta 90 dias',
    desc: 'Notifica RH e gestor quando férias vencerão em 90 dias',
    canal: 'Email + Portal',
    ativo: true,
  },
  {
    id: 'a60',
    titulo: 'Alerta 60 dias',
    desc: 'Segundo aviso com urgência reforçada',
    canal: 'Email + WhatsApp',
    ativo: true,
  },
  {
    id: 'a30',
    titulo: 'Alerta 30 dias',
    desc: 'Alerta crítico — férias prestes a vencer',
    canal: 'Email + SMS + Portal',
    ativo: true,
  },
  {
    id: 'semana',
    titulo: 'Lembrete semana',
    desc: 'Lembrete ao colaborador 7 dias antes do início',
    canal: 'Email + Portal',
    ativo: true,
  },
  {
    id: 'mensal',
    titulo: 'Relatório mensal',
    desc: 'Envio automático do relatório de férias toda 1ª segunda',
    canal: 'Email',
    ativo: false,
  },
  {
    id: 'confirm',
    titulo: 'Confirmação',
    desc: 'Confirmação automática ao aprovar agendamento',
    canal: 'Email + Portal',
    ativo: true,
  },
]

function tone(status?: string) {
  const s = (status || '').toLowerCase()
  if (/(pend|aguard|solicit)/.test(s)) return 'pendente' as const
  if (/(cancel|rejeit|recus)/.test(s)) return 'inativo' as const
  return 'ativo' as const
}

function nomeSaldo(s: FeriasSaldoItem) {
  return s.colaborador_nome || s.nome || '—'
}

function setorSaldo(s: FeriasSaldoItem) {
  return s.departamento || s.setor || '—'
}

function diasSaldo(s: FeriasSaldoItem) {
  if (typeof s.dias_saldo === 'number') return s.dias_saldo
  if (typeof s.saldo_total_dias === 'number') return s.saldo_total_dias
  return 0
}

function diasLimite(s: FeriasSaldoItem): number | null {
  if (typeof s.dias_para_limite === 'number') return s.dias_para_limite
  const d = s.destaque?.dias_para_limite
  return typeof d === 'number' ? d : null
}

function limiteGozo(s: FeriasSaldoItem) {
  return s.limite_gozo || s.destaque?.limite_gozo || s.destaque?.concessivo_fim || null
}

function statusPainel(s: FeriasSaldoItem): string {
  const dest = s.destaque?.status
  if (dest === 'programada') return 'planejada'
  if (s.resumo_status === 'vencidas' || s.tem_vencidas) return 'vencidas'
  if (s.resumo_status === 'a_vencer' || s.tem_a_vencer) return 'proxima'
  const d = diasLimite(s)
  if (typeof d === 'number' && d < 0) return 'vencidas'
  if (typeof d === 'number' && d <= 30) return 'proxima'
  return 'normal'
}

function prioridadeDe(s: FeriasSaldoItem): FeriasPrioridade {
  if (s.prioridade) return s.prioridade
  if (s.tem_vencidas) return 'vencido'
  const d = diasLimite(s)
  if (typeof d === 'number') {
    if (d < 0) return 'vencido'
    if (d <= 10) return 'd10'
    if (d <= 30) return 'd30'
    if (d <= 60) return 'd60'
    if (d <= 90) return 'd90'
  }
  if (s.tem_a_vencer) return 'd60'
  return 'ok'
}

const PRIORIDADE_LABEL: Record<FeriasPrioridade, string> = {
  vencido: 'Vencido',
  d10: '≤ 10 dias',
  d30: '≤ 30 dias',
  d60: '≤ 2 meses',
  d90: '≤ 3 meses',
  ok: 'Em dia',
}

function prioridadeTone(p: FeriasPrioridade): 'inativo' | 'pendente' | 'ativo' {
  if (p === 'vencido' || p === 'd10') return 'inativo'
  if (p === 'd30' || p === 'd60' || p === 'd90') return 'pendente'
  return 'ativo'
}

function labelDiasRestantes(dias: number | null) {
  if (dias == null) return '—'
  if (dias < 0) return `${Math.abs(dias)}d vencido`
  if (dias === 0) return 'Vence hoje'
  return `${dias}d`
}

function downloadCsv(filename: string, rows: string[][]) {
  const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n')
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

type AgendarState = {
  colaborador_id: string
  nome: string
  diasSaldo: number
  vencimento: string | null
  periodo_inicio?: string | null
  periodo_fim?: string | null
}

export function FeriasPage() {
  const { canEdit } = useAuth()
  const editable = canEdit('dp-ferias')
  const qc = useQueryClient()

  const [aba, setAba] = useState<Aba>('painel')
  const [filtroPainel, setFiltroPainel] = useState('todas')
  const [busca, setBusca] = useState('')
  const [statusSolic, setStatusSolic] = useState('')
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState<'success' | 'info' | 'danger'>('info')
  const [agendar, setAgendar] = useState<AgendarState | null>(null)
  const [inicio, setInicio] = useState('')
  const [diasGozo, setDiasGozo] = useState(30)
  const [abono, setAbono] = useState(0)
  const [obs, setObs] = useState('')

  const [salario, setSalario] = useState('')
  const [calcDias, setCalcDias] = useState(30)
  const [calcAbono, setCalcAbono] = useState(0)
  const [calcDep, setCalcDep] = useState(0)
  const [calc, setCalc] = useState<FeriasCalculo | null>(null)
  const [calcColabId, setCalcColabId] = useState('')
  const [calcColabNome, setCalcColabNome] = useState('')
  const [calcCargoNome, setCalcCargoNome] = useState('')
  const [salarioOrigem, setSalarioOrigem] = useState('')
  const [salarioLoading, setSalarioLoading] = useState(false)

  const [autos, setAutos] = useState(AUTOS_SEED)

  const painel = useQuery({
    queryKey: ['dp', 'ferias', 'saldo', 'enterprise'],
    queryFn: () => fetchFeriasSaldoPainel(''),
  })

  const lista = useQuery({
    queryKey: ['dp', 'ferias', statusSolic],
    queryFn: () => fetchFerias(statusSolic || undefined),
  })

  const saldosRaw = painel.data?.dados || []
  const ferias = lista.data?.dados || []

  const mapped = useMemo(() => {
    return saldosRaw.map((s) => ({
      raw: s,
      id: String(s.colaborador_id || ''),
      nome: nomeSaldo(s),
      setor: setorSaldo(s),
      diasSaldo: diasSaldo(s),
      vencimento: limiteGozo(s),
      diasRestantes: diasLimite(s),
      status: statusPainel(s),
      prioridade: prioridadeDe(s),
      agendado: s.destaque?.status === 'programada' ? limiteGozo(s) : null,
      periodo_inicio: (s.destaque as { periodo_inicio?: string } | undefined)?.periodo_inicio,
      periodo_fim: (s.destaque as { periodo_fim?: string } | undefined)?.periodo_fim,
    }))
  }, [saldosRaw])

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase()
    let rows = mapped
    if (filtroPainel !== 'todas') rows = rows.filter((r) => r.status === filtroPainel)
    if (q) {
      rows = rows.filter(
        (r) => r.nome.toLowerCase().includes(q) || r.setor.toLowerCase().includes(q),
      )
    }
    return [...rows].sort((a, b) => {
      const ordem: Record<string, number> = {
        vencidas: 0,
        proxima: 1,
        planejada: 2,
        normal: 3,
      }
      const oa = (ordem[a.status] ?? 9) - (ordem[b.status] ?? 9)
      if (oa !== 0) return oa
      const da = a.diasRestantes ?? 9999
      const db = b.diasRestantes ?? 9999
      return da - db
    })
  }, [mapped, filtroPainel, busca])

  const kpis = useMemo(() => {
    const vencidas = mapped.filter((c) => c.status === 'vencidas').length
    const proximas = mapped.filter((c) => c.status === 'proxima').length
    const planejadas = mapped.filter((c) => c.status === 'planejada').length
    return { vencidas, proximas, planejadas, total: mapped.length }
  }, [mapped])

  const distMes = useMemo(() => {
    const ano = new Date().getFullYear()
    return MESES.map((mes, i) => ({
      mes,
      count: mapped.filter((c) => {
        if (!c.agendado) return false
        const d = new Date(`${c.agendado}T12:00:00`)
        return !Number.isNaN(d.getTime()) && d.getFullYear() === ano && d.getMonth() === i
      }).length,
    }))
  }, [mapped])

  const maxDist = Math.max(...distMes.map((d) => d.count), 1)

  const porSetor = useMemo(() => {
    const map: Record<string, { total: number; vencidas: number }> = {}
    for (const c of mapped) {
      if (!map[c.setor]) map[c.setor] = { total: 0, vencidas: 0 }
      map[c.setor].total++
      if (c.status === 'vencidas') map[c.setor].vencidas++
    }
    return Object.entries(map).sort((a, b) => b[1].total - a[1].total)
  }, [mapped])

  const proximas60 = useMemo(
    () =>
      mapped
        .filter((c) => typeof c.diasRestantes === 'number' && c.diasRestantes >= 0 && c.diasRestantes <= 60)
        .sort((a, b) => (a.diasRestantes ?? 0) - (b.diasRestantes ?? 0)),
    [mapped],
  )

  function flash(text: string, tone: 'success' | 'info' | 'danger' = 'success') {
    setMsgTone(tone)
    setMsg(text)
  }

  const agendarMut = useMutation({
    mutationFn: () => {
      if (!agendar) throw new Error('Selecione um colaborador.')
      return agendarFerias({
        colaborador_id: agendar.colaborador_id,
        data_inicio: inicio,
        dias_ferias: diasGozo,
        dias_abono: abono,
        periodo_inicio: agendar.periodo_inicio,
        periodo_fim: agendar.periodo_fim,
        observacoes: obs,
      })
    },
    onSuccess: async () => {
      flash(`Agendamento de ${agendar?.nome} enviado para aprovação.`)
      setAgendar(null)
      setInicio('')
      setObs('')
      await qc.invalidateQueries({ queryKey: ['dp', 'ferias'] })
    },
    onError: (err) => {
      flash(err instanceof ApiError ? err.message : 'Falha ao agendar.', 'danger')
    },
  })

  const calcMut = useMutation({
    mutationFn: () =>
      simularFerias({
        salario: Number(String(salario).replace(',', '.')),
        dias: calcDias,
        abono: calcAbono,
        dependentes: calcDep,
      }),
    onSuccess: (res) => setCalc(res.resultado),
    onError: (err) => {
      setCalc(null)
      flash(err instanceof ApiError ? err.message : 'Falha ao calcular.', 'danger')
    },
  })

  const aprovarMut = useMutation({
    mutationFn: (id: string) => aprovarFerias(id),
    onSuccess: async () => {
      flash('Férias aprovadas.')
      await qc.invalidateQueries({ queryKey: ['dp', 'ferias'] })
    },
    onError: (err) => flash(err instanceof ApiError ? err.message : 'Falha ao aprovar.', 'danger'),
  })

  const recusarMut = useMutation({
    mutationFn: (id: string) => recusarFerias(id, 'Recusado pelo RH/gestor na SPA'),
    onSuccess: async () => {
      flash('Férias recusadas.', 'info')
      await qc.invalidateQueries({ queryKey: ['dp', 'ferias'] })
    },
    onError: (err) => flash(err instanceof ApiError ? err.message : 'Falha ao recusar.', 'danger'),
  })

  function openAgendar(row: (typeof mapped)[number]) {
    setAgendar({
      colaborador_id: row.id,
      nome: row.nome,
      diasSaldo: row.diasSaldo,
      vencimento: row.vencimento,
      periodo_inicio: row.periodo_inicio,
      periodo_fim: row.periodo_fim,
    })
    setDiasGozo(Math.min(Math.max(row.diasSaldo || 30, 5), 30))
    setAbono(0)
    setInicio('')
    setObs('')
  }

  async function carregarSalarioDoCargo(colaboradorId: string, nomeHint = '') {
    if (!colaboradorId) {
      setCalcColabId('')
      setCalcColabNome('')
      setCalcCargoNome('')
      setSalarioOrigem('')
      setSalario('')
      return
    }
    setSalarioLoading(true)
    setCalcColabId(colaboradorId)
    setCalc(null)
    try {
      const colab = await fetchColaborador(colaboradorId)
      let cargo: CargoDetalhe | null = null
      if (colab.cargo_id) {
        try {
          cargo = await fetchCargo(colab.cargo_id)
        } catch {
          cargo = null
        }
      }
      const resolved = salarioBaseDoCargo(colab, cargo)
      setCalcColabNome(colab.nome || nomeHint)
      setCalcCargoNome(resolved.cargoNome)
      setSalarioOrigem(resolved.origem)
      setSalario(resolved.valor != null ? String(resolved.valor) : '')
      setCalcDep(dependentesIrrf(colab.dependentes))
      const row = mapped.find((m) => m.id === colaboradorId)
      if (row?.diasSaldo) {
        setCalcDias(Math.min(Math.max(row.diasSaldo, 1), 30))
      }
      if (resolved.valor == null) {
        flash(
          `Sem salário no cargo de ${colab.nome || nomeHint}. Informe o valor manualmente.`,
          'info',
        )
      }
    } catch (err) {
      flash(err instanceof ApiError ? err.message : 'Falha ao buscar salário do cargo.', 'danger')
    } finally {
      setSalarioLoading(false)
    }
  }

  function exportar() {
    downloadCsv('ferias-painel.csv', [
      ['Colaborador', 'Setor', 'Saldo', 'Vencimento', 'Restam', 'Status'],
      ...filtrados.map((r) => [
        r.nome,
        r.setor,
        String(r.diasSaldo),
        r.vencimento || '',
        labelDiasRestantes(r.diasRestantes),
        r.status,
      ]),
    ])
    flash('Exportação CSV gerada.', 'info')
  }

  function gerarRelatorio(id: string) {
    if (id === 'vencimentos') {
      downloadCsv('ferias-vencimentos.csv', [
        ['Colaborador', 'Setor', 'Saldo', 'Limite', 'Restam'],
        ...mapped
          .filter((r) => r.status === 'vencidas' || r.status === 'proxima')
          .map((r) => [
            r.nome,
            r.setor,
            String(r.diasSaldo),
            r.vencimento || '',
            labelDiasRestantes(r.diasRestantes),
          ]),
      ])
    } else if (id === 'setor') {
      downloadCsv('ferias-por-setor.csv', [
        ['Setor', 'Total', 'Vencidas'],
        ...porSetor.map(([setor, d]) => [setor, String(d.total), String(d.vencidas)]),
      ])
    } else {
      downloadCsv(`ferias-${id}.csv`, [
        ['Colaborador', 'Setor', 'Saldo', 'Status'],
        ...mapped.map((r) => [r.nome, r.setor, String(r.diasSaldo), r.status]),
      ])
    }
    flash(`Relatório “${id}” exportado.`, 'success')
  }

  const chips = [
    { id: 'todas', label: 'Todos', count: mapped.length },
    { id: 'vencidas', label: 'Vencidas', count: kpis.vencidas },
    { id: 'proxima', label: 'Próximas', count: kpis.proximas },
    { id: 'planejada', label: 'Planejadas', count: kpis.planejadas },
    { id: 'normal', label: 'Regular', count: mapped.filter((c) => c.status === 'normal').length },
  ]

  return (
    <section className="hi-page">
      <PageHeader
        title="Férias"
        subtitle="Gestão enterprise — painel, planejamento, calculadora CLT, relatórios e automações (ideias do legado)."
        actions={
          <>
            <a className="btn btn-secondary" href={legacyAppUrl('#/dp-ferias-enterprise')}>
              Legado
            </a>
            <button type="button" className="btn btn-secondary" onClick={exportar}>
              Exportar
            </button>
            {editable ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setAba('painel')
                  flash('Selecione um colaborador no painel para agendar.', 'info')
                }}
              >
                Agendar férias
              </button>
            ) : null}
          </>
        }
      />

      {msg ? <Alert tone={msgTone}>{msg}</Alert> : null}

      <KpiGrid>
        <Kpi label="Vencidas" value={String(kpis.vencidas)} />
        <Kpi label="Próx. 30d" value={String(kpis.proximas)} />
        <Kpi label="Planejadas" value={String(kpis.planejadas)} />
        <Kpi label="No painel" value={String(kpis.total)} />
      </KpiGrid>

      {kpis.vencidas > 0 ? (
        <Alert tone="danger">
          {kpis.vencidas} colaborador(es) com férias vencidas — risco trabalhista.{' '}
          {editable ? (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() =>
                flash(`${kpis.vencidas} alerta(s) de férias vencidas sinalizados para gestores.`, 'info')
              }
            >
              Notificar gestores
            </button>
          ) : null}
        </Alert>
      ) : null}

      <div className="hi-ferias-tabs" role="tablist" aria-label="Abas de férias">
        {ABAS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={aba === t.id}
            className={`hi-ferias-tab${aba === t.id ? ' is-active' : ''}`}
            onClick={() => setAba(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {aba === 'painel' ? (
        <>
          <div className="hi-ferias-chip-row">
            {chips.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`hi-ferias-chip${filtroPainel === c.id ? ' is-active' : ''}`}
                onClick={() => setFiltroPainel(c.id)}
              >
                {c.label} ({c.count})
              </button>
            ))}
          </div>
          <form
            className="hi-filters"
            onSubmit={(e) => {
              e.preventDefault()
            }}
          >
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar nome ou setor…"
              aria-label="Buscar no painel"
            />
          </form>
          <Section title={`Painel (${filtrados.length})`} flush>
            {painel.isLoading ? <Muted>Carregando…</Muted> : null}
            {painel.isError ? (
              <Alert tone="danger">
                {painel.error instanceof ApiError ? painel.error.message : 'Falha no painel.'}
              </Alert>
            ) : null}
            {!painel.isLoading && filtrados.length === 0 ? <Empty>Nenhum colaborador neste filtro.</Empty> : null}
            {filtrados.length > 0 ? (
              <DataTable>
                <thead>
                  <tr>
                    <th>Colaborador</th>
                    <th>Setor</th>
                    <th>Saldo</th>
                    <th>Vencimento</th>
                    <th>Prioridade</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map((r) => (
                    <tr key={r.id || r.nome}>
                      <td>
                        <strong>{r.nome}</strong>
                      </td>
                      <td>{r.setor}</td>
                      <td>
                        <span
                          className={
                            r.diasSaldo < 15 ? 'hi-ferias-saldo-warn' : 'hi-ferias-saldo-ok'
                          }
                        >
                          {r.diasSaldo}d
                        </span>
                      </td>
                      <td>
                        {formatDate(r.vencimento)}
                        <br />
                        <Muted>{labelDiasRestantes(r.diasRestantes)}</Muted>
                      </td>
                      <td>
                        <StatusBadge tone={prioridadeTone(r.prioridade)}>
                          {PRIORIDADE_LABEL[r.prioridade]}
                        </StatusBadge>
                      </td>
                      <td>
                        {editable && r.id ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => openAgendar(r)}
                            >
                              Agendar
                            </button>{' '}
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() =>
                                flash(`Alerta registrado sobre férias de ${r.nome}.`, 'info')
                              }
                            >
                              Notificar
                            </button>
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            ) : null}
          </Section>
        </>
      ) : null}

      {aba === 'planejamento' ? (
        <div className="hi-stack">
          <div className="hi-ferias-grid-2">
            <Section title={`Distribuição por mês — ${new Date().getFullYear()}`}>
              <div className="hi-ferias-bars">
                {distMes.map((d) => (
                  <div key={d.mes} className="hi-ferias-bar-row">
                    <span>{d.mes}</span>
                    <div className="hi-ferias-bar-track">
                      <div
                        className="hi-ferias-bar-fill"
                        style={{ width: `${(d.count / maxDist) * 100}%` }}
                      />
                    </div>
                    <span>{d.count}</span>
                  </div>
                ))}
              </div>
            </Section>
            <Section title="Fluxo de aprovação">
              <div className="hi-ferias-flow">
                {[
                  ['1', 'Colaborador solicita', 'Solicitação com período desejado'],
                  ['2', 'Gestor analisa', 'Aprovação ou recusa com justificativa'],
                  ['3', 'RH valida', 'Saldo, conflitos e legislação'],
                  ['4', 'Aprovação final', 'Confirmação e registro'],
                ].map(([n, t, d]) => (
                  <div key={n} className="hi-ferias-flow-item">
                    <span className="hi-ferias-flow-n">{n}</span>
                    <div>
                      <strong>{t}</strong>
                      <Muted>{d}</Muted>
                    </div>
                  </div>
                ))}
              </div>
              {editable ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ marginTop: '0.75rem' }}
                  onClick={() => {
                    setAba('painel')
                    flash('Selecione um colaborador no painel.', 'info')
                  }}
                >
                  Iniciar agendamento
                </button>
              ) : null}
            </Section>
          </div>
          <Section title="Agenda — próximos agendamentos" flush>
            {mapped.filter((c) => c.agendado).length === 0 ? (
              <Empty>Nenhum agendamento programado no painel.</Empty>
            ) : (
              <DataTable>
                <thead>
                  <tr>
                    <th>Colaborador</th>
                    <th>Setor</th>
                    <th>Início</th>
                    <th>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {mapped
                    .filter((c) => c.agendado)
                    .map((c) => (
                      <tr key={c.id}>
                        <td>{c.nome}</td>
                        <td>{c.setor}</td>
                        <td>{formatDate(c.agendado)}</td>
                        <td>{c.diasSaldo}d</td>
                      </tr>
                    ))}
                </tbody>
              </DataTable>
            )}
          </Section>
        </div>
      ) : null}

      {aba === 'calculadora' ? (
        <div className="hi-ferias-grid-2">
          <Section title="Férias vencendo em até 60 dias" flush>
            <div className="card-body" style={{ paddingBottom: 0 }}>
              <Muted>
                Clique no colaborador para carregar salário, dias e dependentes na calculadora.
              </Muted>
            </div>
            {proximas60.length === 0 ? (
              <Empty>Nenhuma férias vencendo nos próximos 60 dias.</Empty>
            ) : (
              <DataTable>
                <thead>
                  <tr>
                    <th>Colaborador</th>
                    <th>Saldo</th>
                    <th>Restam</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {proximas60.map((c) => {
                    const selected = calcColabId === c.id
                    return (
                      <tr
                        key={c.id}
                        className={`hi-ferias-calc-row${selected ? ' is-selected' : ''}`}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selected}
                        aria-label={`Selecionar ${c.nome} para calcular férias`}
                        onClick={() => {
                          if (!c.id || salarioLoading) return
                          void carregarSalarioDoCargo(c.id, c.nome)
                        }}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && c.id && !salarioLoading) {
                            e.preventDefault()
                            void carregarSalarioDoCargo(c.id, c.nome)
                          }
                        }}
                      >
                        <td>
                          <strong>{c.nome}</strong>
                          <Muted>{c.setor}</Muted>
                        </td>
                        <td>{c.diasSaldo}d</td>
                        <td>{labelDiasRestantes(c.diasRestantes)}</td>
                        <td>
                          {editable && c.id ? (
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={(e) => {
                                e.stopPropagation()
                                openAgendar(c)
                              }}
                            >
                              Agendar
                            </button>
                          ) : selected ? (
                            '✓'
                          ) : (
                            'Selecionar'
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </DataTable>
            )}
          </Section>
          <Section title="Calcular férias — simulação CLT">
            <div className="hi-fields">
              <label className="hi-field">
                <span>Colaborador (todo o painel)</span>
                <select
                  value={calcColabId}
                  disabled={salarioLoading}
                  onChange={(e) => {
                    const id = e.target.value
                    const row = mapped.find((m) => m.id === id)
                    void carregarSalarioDoCargo(id, row?.nome || '')
                  }}
                >
                  <option value="">Selecione ou clique na lista ao lado…</option>
                  {mapped
                    .filter((c) => c.id)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nome}
                        {c.setor && c.setor !== '—' ? ` — ${c.setor}` : ''}
                      </option>
                    ))}
                </select>
              </label>
              {calcColabId ? (
                <Alert tone="info">
                  {salarioLoading
                    ? 'Carregando dados do colaborador…'
                    : [
                        calcColabNome,
                        calcCargoNome ? `Cargo: ${calcCargoNome}` : null,
                        salarioOrigem || null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                </Alert>
              ) : (
                <Muted>Clique em um colaborador à esquerda para preencher a simulação.</Muted>
              )}
              <label className="hi-field">
                <span>Salário base (R$)</span>
                <input
                  type="number"
                  step="0.01"
                  value={salario}
                  disabled={salarioLoading}
                  onChange={(e) => {
                    setSalario(e.target.value)
                    if (salarioOrigem) setSalarioOrigem('Valor ajustado manualmente')
                  }}
                />
              </label>
              <label className="hi-field">
                <span>Dias de férias</span>
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={calcDias}
                  onChange={(e) => setCalcDias(Number(e.target.value) || 30)}
                />
              </label>
              <label className="hi-field">
                <span>Dias de abono</span>
                <input
                  type="number"
                  min={0}
                  max={10}
                  value={calcAbono}
                  onChange={(e) => setCalcAbono(Number(e.target.value) || 0)}
                />
              </label>
              <label className="hi-field">
                <span>Dependentes IRRF</span>
                <input
                  type="number"
                  min={0}
                  value={calcDep}
                  onChange={(e) => setCalcDep(Number(e.target.value) || 0)}
                />
              </label>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              style={{ marginTop: '0.75rem' }}
              disabled={calcMut.isPending || salarioLoading}
              onClick={() => {
                if (!Number(String(salario).replace(',', '.'))) {
                  flash(
                    calcColabId
                      ? 'Salário do cargo não encontrado. Informe o valor.'
                      : 'Selecione um colaborador ou informe o salário base.',
                    'danger',
                  )
                  return
                }
                if (calcDias + calcAbono > 30) {
                  flash('Dias de gozo + abono não pode exceder 30.', 'danger')
                  return
                }
                calcMut.mutate()
              }}
            >
              {calcMut.isPending ? 'Calculando…' : 'Calcular férias'}
            </button>
            {calc ? (
              <div className="hi-ferias-calc-resultado">
                {(calc.memoria_calculo || []).map((l, i) => (
                  <div
                    key={`${l.item}-${i}`}
                    className={`hi-ferias-calc-linha${l.tipo === 'desconto' ? ' is-desconto' : ''}${
                      l.tipo === 'total' ? ' is-total' : ''
                    }`}
                  >
                    <span>
                      {l.sinal ? `${l.sinal} ` : ''}
                      {l.item}
                    </span>
                    <span>{formatMoney(l.valor)}</span>
                  </div>
                ))}
                {!calc.memoria_calculo?.length ? (
                  <>
                    <div className="hi-ferias-calc-linha">
                      <span>Férias</span>
                      <span>{formatMoney(calc.valor_ferias)}</span>
                    </div>
                    <div className="hi-ferias-calc-linha">
                      <span>⅓ constitucional</span>
                      <span>{formatMoney(calc.valor_terco)}</span>
                    </div>
                    <div className="hi-ferias-calc-linha is-desconto">
                      <span>INSS</span>
                      <span>{formatMoney(calc.valor_inss)}</span>
                    </div>
                    <div className="hi-ferias-calc-linha is-desconto">
                      <span>IRRF</span>
                      <span>{formatMoney(calc.valor_irrf)}</span>
                    </div>
                    <div className="hi-ferias-calc-linha is-total">
                      <span>Líquido</span>
                      <span>{formatMoney(calc.valor_liquido)}</span>
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}
          </Section>
        </div>
      ) : null}

      {aba === 'solicitacoes' ? (
        <>
          <form
            className="hi-filters"
            onSubmit={(e) => {
              e.preventDefault()
            }}
          >
            <select
              value={statusSolic}
              onChange={(e) => setStatusSolic(e.target.value)}
              aria-label="Status solicitações"
            >
              <option value="">Todos os status</option>
              <option value="pendente">Pendentes</option>
              <option value="aprovada">Aprovadas</option>
              <option value="em_gozo">Em gozo</option>
              <option value="recusada">Recusadas</option>
              <option value="cancelada">Canceladas</option>
            </select>
          </form>
          <Section title="Solicitações" flush>
            {lista.isLoading ? <Muted>Carregando…</Muted> : null}
            {lista.isError ? (
              <Alert tone="danger">
                {lista.error instanceof ApiError ? lista.error.message : 'Falha ao listar.'}
              </Alert>
            ) : null}
            {!lista.isLoading && ferias.length === 0 ? <Empty>Nenhuma solicitação.</Empty> : null}
            {ferias.length > 0 ? (
              <DataTable>
                <thead>
                  <tr>
                    <th>Colaborador</th>
                    <th>Gozo</th>
                    <th>Dias</th>
                    <th>Líquido</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {ferias.map((f) => (
                    <tr key={f.id}>
                      <td>{f.colaborador_nome || '—'}</td>
                      <td>
                        {formatDate(f.gozo_inicio || f.data_inicio)} –{' '}
                        {formatDate(f.gozo_fim || f.data_fim)}
                      </td>
                      <td>{f.dias_solicitados ?? f.dias ?? '—'}</td>
                      <td>{formatMoney(f.valor_liquido)}</td>
                      <td>
                        <StatusBadge tone={tone(f.status)}>
                          {f.status_display || f.status || '—'}
                        </StatusBadge>
                      </td>
                      <td>
                        {editable && f.status === 'pendente' ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              disabled={aprovarMut.isPending}
                              onClick={() => aprovarMut.mutate(f.id)}
                            >
                              Aprovar
                            </button>{' '}
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              disabled={recusarMut.isPending}
                              onClick={() => recusarMut.mutate(f.id)}
                            >
                              Recusar
                            </button>
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            ) : null}
          </Section>
        </>
      ) : null}

      {aba === 'relatorios' ? (
        <div className="hi-stack">
          <div className="hi-ferias-grid-2">
            <Section title="Por setor" flush>
              {porSetor.length === 0 ? (
                <Empty>Sem dados.</Empty>
              ) : (
                <DataTable>
                  <thead>
                    <tr>
                      <th>Setor</th>
                      <th>Total</th>
                      <th>Vencidas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porSetor.map(([setor, d]) => (
                      <tr key={setor}>
                        <td>{setor}</td>
                        <td>{d.total}</td>
                        <td>{d.vencidas}</td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              )}
            </Section>
            <Section title="Por status">
              {[
                ['Vencidas', kpis.vencidas],
                ['Próx. venc.', kpis.proximas],
                ['Planejadas', kpis.planejadas],
                ['Regular', mapped.filter((c) => c.status === 'normal').length],
              ].map(([label, val]) => (
                <div
                  key={String(label)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.4rem 0',
                    borderBottom: '1px solid var(--hi-border, #eef1f6)',
                  }}
                >
                  <span>{label}</span>
                  <strong>{val}</strong>
                </div>
              ))}
            </Section>
          </div>
          <div className="hi-ferias-rel-grid">
            {[
              ['vencimentos', 'Relatório de vencimentos', 'Vencidas e próximas'],
              ['setor', 'Relatório por setor', 'Consolidado por área'],
              ['mes', 'Relatório por mês', 'Distribuição mensal'],
              ['geral', 'Relatório geral', 'Painel completo'],
            ].map(([id, titulo, desc]) => (
              <button
                key={id}
                type="button"
                className="hi-ferias-rel-card"
                onClick={() => gerarRelatorio(id)}
              >
                <strong>{titulo}</strong>
                <Muted>{desc}</Muted>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {aba === 'automacoes' ? (
        <div className="hi-stack">
          <Alert tone="info">
            Automações alinhadas ao Art. 134 CLT — férias no período concessivo. Toggles locais
            (preferências de tela); disparo real via jobs/notificações na próxima onda.
          </Alert>
          {autos.map((a) => (
            <div key={a.id} className="hi-ferias-auto">
              <div className="hi-ferias-auto-body">
                <strong>{a.titulo}</strong>
                <Muted>{a.desc}</Muted>
                <Muted>Canal: {a.canal}</Muted>
              </div>
              <label className="hi-check">
                <input
                  type="checkbox"
                  checked={a.ativo}
                  onChange={(e) => {
                    const on = e.target.checked
                    setAutos((list) =>
                      list.map((x) => (x.id === a.id ? { ...x, ativo: on } : x)),
                    )
                    flash(on ? `${a.titulo} ativada.` : `${a.titulo} desativada.`, 'info')
                  }}
                />
                {a.ativo ? 'Ativo' : 'Inativo'}
              </label>
            </div>
          ))}
        </div>
      ) : null}

      {agendar ? (
        <div
          className="hi-ferias-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) setAgendar(null)
          }}
        >
          <div className="hi-ferias-modal" role="dialog" aria-modal="true">
            <div className="hi-ferias-modal-head">
              <h3>Agendar férias — {agendar.nome}</h3>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAgendar(null)}>
                Fechar
              </button>
            </div>
            <Alert tone="info">
              Saldo: <strong>{agendar.diasSaldo} dias</strong>
              {agendar.vencimento ? (
                <>
                  {' '}
                  · Vencimento: <strong>{formatDate(agendar.vencimento)}</strong>
                </>
              ) : null}
            </Alert>
            <div className="hi-fields">
              <label className="hi-field">
                <span>Data de início *</span>
                <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />
              </label>
              <label className="hi-field">
                <span>Dias de gozo *</span>
                <input
                  type="number"
                  min={5}
                  max={30}
                  value={diasGozo}
                  onChange={(e) => setDiasGozo(Number(e.target.value) || 30)}
                />
              </label>
              <label className="hi-field">
                <span>Dias de abono</span>
                <input
                  type="number"
                  min={0}
                  max={10}
                  value={abono}
                  onChange={(e) => setAbono(Number(e.target.value) || 0)}
                />
              </label>
              <label className="hi-field hi-span-2">
                <span>Observações</span>
                <textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={2} />
              </label>
            </div>
            <Muted>Fluxo: Colaborador → Gestor → RH → Aprovação final</Muted>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setAgendar(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={agendarMut.isPending}
                onClick={() => {
                  if (!inicio) {
                    flash('Informe a data de início.', 'danger')
                    return
                  }
                  agendarMut.mutate()
                }}
              >
                {agendarMut.isPending ? 'Enviando…' : 'Enviar para aprovação'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}

export default FeriasPage
